import { useEffect, useState, useCallback } from "react";
import { api, CHART } from "@/lib/api";
import { Card, Overline, SegmentChip } from "@/components/common";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { PieChart, Pie, Cell, ResponsiveContainer } from "recharts";
import { Search, Phone, Mail, Sparkle, Check, Calendar, Clock } from "lucide-react";

const SEGMENTS = ["All", "VIP", "Frequent", "Regular", "Occasional", "At Risk", "New"];

export default function Customers() {
  const [search, setSearch] = useState("");
  const [segment, setSegment] = useState("All");
  const [data, setData] = useState(null);
  const [sel, setSel] = useState(null);
  const [offerSent, setOfferSent] = useState(false);

  const load = useCallback(() => {
    api.get(`/customers?search=${encodeURIComponent(search)}&segment=${segment}`).then(({ data }) => setData(data));
  }, [search, segment]);
  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [load]);

  const open = async (row) => {
    setOfferSent(false);
    const { data } = await api.get(`/customers/${row.id}`);
    setSel(data);
  };
  const sendOffer = async () => { await api.post(`/customers/${sel.id}/offer`); setOfferSent(true); toast.success("Offer sent", { description: sel.name }); };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-3xl font-semibold tracking-tight text-[#1C1F1E]">Customers</h1>
        <p className="text-[#52525B] mt-1">{data?.total || 0} golfers in your book of business.</p>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[240px] max-w-sm">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A1A1AA]" />
          <input data-testid="customer-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by name or email..."
            className="w-full pl-9 pr-3 py-2 rounded-lg border border-[#E5E7EB] bg-white text-sm outline-none focus:border-[#0F3F2D]/40 transition-colors" />
        </div>
        <div className="flex flex-wrap gap-1.5">
          {SEGMENTS.map((s) => (
            <button key={s} data-testid={`segment-${s.toLowerCase().replace(/[^a-z]/g, "-")}`} onClick={() => setSegment(s)}
              className={cn("px-3 py-1.5 rounded-full text-sm font-medium border transition-colors",
                segment === s ? "bg-[#0F3F2D] text-white border-transparent" : "bg-white text-[#52525B] border-[#E5E7EB] hover:border-[#0F3F2D]/30")}>
              {s}{s !== "All" && data?.counts?.[s] !== undefined && <span className="ml-1.5 text-xs opacity-60">{data.counts[s]}</span>}
            </button>
          ))}
        </div>
      </div>

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b border-[#E5E7EB] text-left">
              {["Name", "Rounds YTD", "Lifetime Spend", "Avg Spend", "Last Visit", "Preferred Time", "Segment"].map((h) => <th key={h} className="px-5 py-3 overline font-semibold text-[#71717A]">{h}</th>)}
            </tr></thead>
            <tbody>
              {data?.rows.map((c) => (
                <tr key={c.id} data-testid={`customer-row`} onClick={() => open(c)} className="border-b border-[#F1F3F2] last:border-0 hover:bg-[#0F3F2D]/[0.02] cursor-pointer transition-colors">
                  <td className="px-5 py-3 font-medium text-[#1C1F1E]">{c.name}</td>
                  <td className="px-5 py-3 font-mono2 text-[#52525B]">{c.rounds_ytd}</td>
                  <td className="px-5 py-3 font-mono2 font-semibold text-[#0F3F2D]">${c.lifetime_spend.toLocaleString()}</td>
                  <td className="px-5 py-3 font-mono2 text-[#52525B]">${c.avg_spend}</td>
                  <td className="px-5 py-3 text-[#52525B]">{c.days_since_visit}d ago</td>
                  <td className="px-5 py-3 text-[#52525B]">{c.preferred_time}</td>
                  <td className="px-5 py-3"><SegmentChip segment={c.segment} /></td>
                </tr>
              ))}
              {data?.rows.length === 0 && <tr><td colSpan={7} className="px-5 py-16 text-center text-[#A1A1AA]">No golfers match your search.</td></tr>}
            </tbody>
          </table>
        </div>
      </Card>

      <Sheet open={!!sel} onOpenChange={(o) => !o && setSel(null)}>
        <SheetContent side="right" className="w-full sm:max-w-lg p-0 overflow-y-auto bg-white/95 backdrop-blur-2xl" data-testid="customer-panel">
          <SheetTitle className="sr-only">Customer Profile</SheetTitle>
          {sel && (
            <div>
              <div className="px-6 py-6 sidebar-grain text-white">
                <SegmentChip segment={sel.segment} className="!bg-white/10 !text-white !border-white/20" />
                <div className="font-display text-2xl font-semibold mt-3">{sel.name}</div>
                <div className="mt-2 space-y-1 text-sm text-white/60">
                  <div className="flex items-center gap-2"><Phone size={13} />{sel.phone}</div>
                  <div className="flex items-center gap-2"><Mail size={13} />{sel.email}</div>
                </div>
              </div>

              <div className="p-6 space-y-6">
                <div className="grid grid-cols-3 gap-3">
                  <Metric label="Rounds YTD" value={sel.rounds_ytd} />
                  <Metric label="Lifetime Value" value={`$${sel.lifetime_spend.toLocaleString()}`} accent />
                  <Metric label="Avg Spend" value={`$${sel.avg_spend}`} />
                </div>

                <div>
                  <Overline>Spend Breakdown</Overline>
                  <div className="flex items-center gap-5 mt-3">
                    <div className="h-32 w-32 shrink-0">
                      <ResponsiveContainer><PieChart>
                        <Pie data={[{ n: "Green Fees", v: sel.green_spend }, { n: "F&B", v: sel.fnb_spend }, { n: "Pro Shop", v: sel.shop_spend }]} dataKey="v" innerRadius={36} outerRadius={60} paddingAngle={2} strokeWidth={0}>
                          {[0, 1, 2].map((i) => <Cell key={i} fill={CHART[i]} />)}
                        </Pie>
                      </PieChart></ResponsiveContainer>
                    </div>
                    <div className="flex-1 space-y-2 text-sm">
                      {[["Green Fee", sel.green_spend, CHART[0]], ["F&B", sel.fnb_spend, CHART[1]], ["Pro Shop", sel.shop_spend, CHART[2]]].map(([l, v, c]) => (
                        <div key={l} className="flex items-center justify-between">
                          <span className="flex items-center gap-2 text-[#52525B]"><span className="w-2.5 h-2.5 rounded-sm" style={{ background: c }} />{l}</span>
                          <span className="font-mono2 text-[#1C1F1E]">${v.toLocaleString()}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="rounded-lg border border-[#E5E7EB] p-3"><div className="flex items-center gap-1.5 text-xs text-[#A1A1AA]"><Calendar size={12} />Preferred Days</div><div className="font-medium text-[#1C1F1E] mt-1 text-sm">{sel.preferred_days?.join(", ")}</div></div>
                  <div className="rounded-lg border border-[#E5E7EB] p-3"><div className="flex items-center gap-1.5 text-xs text-[#A1A1AA]"><Clock size={12} />Preferred Tee Times</div><div className="font-medium text-[#1C1F1E] mt-1 text-sm">{sel.preferred_tee_times}</div></div>
                </div>

                <div>
                  <Overline>Golfer Record</Overline>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-2" data-testid="customer-record">
                    <Metric label="Frequency" value={sel.frequency} />
                    <Metric label="Booking Source" value={sel.booking_source} />
                    <Metric label="No-Shows" value={sel.no_shows} />
                    <Metric label="Cancellations" value={sel.cancellations} />
                  </div>
                </div>

                <div>
                  <Overline>Recent Bookings</Overline>
                  <div className="mt-2 space-y-1.5">
                    {sel.recent_bookings.map((b, i) => (
                      <div key={i} className="flex items-center justify-between text-sm py-1.5 border-b border-[#F1F3F2] last:border-0">
                        <span className="text-[#52525B] font-mono2">{b.date}</span>
                        <span className="text-[#A1A1AA]">{b.players} players</span>
                        <span className="font-mono2 text-[#1C1F1E]">${b.total}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="rounded-xl border border-[#0F3F2D]/15 bg-[#0F3F2D]/[0.04] p-4">
                  <div className="flex items-center gap-1.5 mb-1.5"><Sparkle size={13} className="text-[#C7A24A]" /><Overline className="!text-[#0F3F2D]">ScratchHQ Customer Insight</Overline></div>
                  <p className="text-sm text-[#1C1F1E] leading-relaxed">{sel.insight}</p>
                  <div className="mt-3">
                    {offerSent ? (
                      <span className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#059669]/10 text-[#059669] text-sm font-medium border border-[#059669]/20"><Check size={15} />{sel.insight_action} Sent</span>
                    ) : (
                      <Button data-testid="send-offer-btn" onClick={sendOffer} className="bg-[#0F3F2D] hover:bg-[#165c42] rounded-lg h-9">{sel.insight_action}</Button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}

const Metric = ({ label, value, accent }) => (
  <div className="rounded-lg border border-[#E5E7EB] p-3">
    <div className="text-xs text-[#A1A1AA]">{label}</div>
    <div className={cn("font-mono2 font-semibold mt-1", accent ? "text-[#0F3F2D] text-lg" : "text-[#1C1F1E]")}>{value}</div>
  </div>
);
